import {
  BottomSheet,
  CbhIcon,
  Divider,
  Text,
  useModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { type TransparencyPayRate } from "../../../types/payRate";
import { getOfferedJobTypes } from "../../../utils/pay-transparency";
import { PayAndScheduleFormV2 } from "../../PayAndScheduleFormV2/PayAndScheduleFormV2";
import { PayDetailsSection } from "./PayDetailsSection";
import { PlacementSectionTemplate } from "./PlacementSectionTemplate";

interface PayAvailabilityDetailsSectionProps {
  placement: PlacementWithDetails;
  placementCandidate: PlacementCandidate;
  isPayTransparencyAvailable: boolean;
  highestMatchingQualification: string;
  transparencyPayRates: TransparencyPayRate[];
}

export function PayAvailabilityDetailsSection(props: PayAvailabilityDetailsSectionProps) {
  const {
    placement,
    placementCandidate,
    isPayTransparencyAvailable,
    highestMatchingQualification,
    transparencyPayRates,
  } = props;

  const verifiedModalState = useModalState();

  if (isPayTransparencyAvailable) {
    return (
      <>
        <Box id="payAndSchedule">
          <Stack
            spacing={5}
            sx={{
              backgroundColor: (theme) => theme.palette.background.quaternary,
              mx: -6, // Make the background color go all the way to the sides of the screen
              p: 7,
            }}
          >
            <Stack direction="row" spacing={4} width="100%">
              <PlacementSectionTemplate title="Pay availability" iconType="coin" />
              <Stack
                direction="row"
                spacing={1}
                onClick={() => {
                  logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAY_AND_SCHEDULE_FORM_ACTIONS, {
                    action: "verified_rates_modal_opened",
                    placementId: placement.id,
                    placementCandidateId: placementCandidate.id,
                    workerId: placementCandidate.worker.id,
                    workplaceId: placement.workplace.id,
                    highestMatchingQualification,
                    offeredJobTypes: getOfferedJobTypes({
                      placement: { jobShifts: placement.jobShifts },
                      transparencyPayRates,
                    }),
                  });

                  verifiedModalState.openModal();
                }}
              >
                <Box sx={{ pt: "2px" }}>
                  <CbhIcon type="verified-colored" size="small" />
                </Box>
                <Box
                  sx={{
                    pb: 1,
                    borderBottom: (theme) => `1px solid ${theme.palette.border?.primary}`,
                    height: "max-content",
                  }}
                >
                  <Text semibold variant="body2" sx={{ color: (theme) => theme.palette.info.main }}>
                    Verified
                  </Text>
                </Box>
              </Stack>
            </Stack>
            <PayAndScheduleFormV2
              transparencyPayRates={transparencyPayRates}
              highestMatchingQualification={highestMatchingQualification}
              placement={{
                id: placement.id,
                workplaceId: placement.workplace.id,
                workerTypes: placement.workerTypes,
                jobShifts: placement.jobShifts,
                transparencyPayRates: placement.transparencyPayRates,
                attributes: { payRange: placement.payRange },
              }}
              placementCandidate={placementCandidate}
              workerId={placementCandidate.worker.id}
            />
          </Stack>
        </Box>
        <BottomSheet
          modalState={verifiedModalState}
          onClose={() => {
            verifiedModalState.closeModal();
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", py: 6, px: 7 }}>
            <IconButton
              size="small"
              iconType="close"
              variant="outlined"
              onClick={() => {
                verifiedModalState.closeModal();
              }}
            />
          </Box>

          <Stack spacing={5} alignItems="center" justifyContent="center" px={6} pb={10}>
            <CbhIcon type="verified-colored" size="xLarge" />
            <Text variant="h4" textAlign="center">
              Verified rates
            </Text>
            <Text variant="body1" sx={{ textAlign: "center", pt: 1, px: 2, pb: 6 }}>
              This workplace has shared the exact rates for their available job types and shift
              times.
            </Text>
          </Stack>
        </BottomSheet>
      </>
    );
  }

  return (
    <>
      <Divider id="payAndSchedule" color="primary" />
      <PayDetailsSection placement={placement} />
    </>
  );
}
